import './SideNavigation.scss';
import { useTranslator } from '@Localization';
import { IconButton } from '@mui/joy';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { EnableFor } from '../../__legacy__/containers/EnableForRole';
import { useDatasetPermissions } from '../../__legacy__/modules/datasets/datasets.permissions';
import { useIntegrationsPermissions } from '../../__legacy__/modules/integration/integrations.permissions';
import { useTeamPermissions } from '../../__legacy__/modules/team/team.permissions';
import { UserRole } from '../../__legacy__/modules/user/user.store';
import { ACDLIo } from '../../__legacy__/routes/config/ExternalLinks';
import { Home } from '../../__legacy__/routes/config/Home';
import { AttachmentIcon } from '../../design/static/icons/AttachmentIcon';
import { ChartBarIcon } from '../../design/static/icons/ChartBarIcon';
import { CloudOutlineIcon } from '../../design/static/icons/CloudOutlineIcon';
import { EvaluationIcon } from '../../design/static/icons/EvaluationIcon';
import { FirstPageIcon } from '../../design/static/icons/FirstPageIcon';
import { FolderIcon } from '../../design/static/icons/FolderIcon';
import { HelpOutlineIcon } from '../../design/static/icons/HelpOutlineIcon';
import { LastPageIcon } from '../../design/static/icons/LastPageIcon';
import { ModelIcon } from '../../design/static/icons/ModelIcon';
import { PersonOutlineIcon } from '../../design/static/icons/PersonOutlineIcon';
import { TeamsIcon } from '../../design/static/icons/TeamsIcon';
import { ZebraSmallLogo } from '../../design/static/logos/ZebraSmallLogo';
import { useAttachmentsPermissions } from '../attachments/Attachments.permissions';
import { useJobPermissions } from '../jobs/jobPermissions';
import { SideNavigationExternalItem } from './SideNavigationExternalItem';
import { SideNavigationItem } from './SideNavigationItem';

interface ISideNavProps {
  isExpanded: boolean;
  userRole: UserRole;
  workspaceId: string;
  handleToggleExpand: () => void;
}

const SideNavigationSeparator = (): ReactElement => <div className='side-navigation-separator' />;

export const SideNavigation = observer((props: ISideNavProps): ReactElement => {
  const { t } = useTranslator();

  const datasetsPermissions = useDatasetPermissions();
  const teamsPermissions = useTeamPermissions();
  const attachmentsPermissions = useAttachmentsPermissions();
  const integrationsPermissions = useIntegrationsPermissions();
  const jobPermissions = useJobPermissions();

  const canViewDatasets = datasetsPermissions.canViewDatasets();
  const canViewTeams = teamsPermissions.canViewTeamMembers();
  const canViewAttachments = attachmentsPermissions.canViewAttachments();
  const canViewIntegrations = integrationsPermissions.canViewIntegrations();

  return (
    <div className={classNames({ expanded: props.isExpanded }, 'side-navigation')}>
      <div className='side-navigation-buttons'>
        <EnableFor hasPermission={canViewTeams}>
          <SideNavigationItem
            id='side-nav-team'
            name={t('team')}
            icon={TeamsIcon}
            link={Home.Team.List.withParams({ workspaceId: props.workspaceId })}
            highlightLink={Home.Team.Path}
            disabled={false}
            isExpanded={props.isExpanded}
          />
        </EnableFor>
        <SideNavigationSeparator />
        <EnableFor hasPermission={canViewDatasets}>
          <SideNavigationItem
            id='side-nav-datasets'
            name={t('datasets')}
            icon={ChartBarIcon}
            link={Home.Datasets.List.withParams({ workspaceId: props.workspaceId })}
            highlightLink={Home.Datasets.Path}
            disabled={false}
            isExpanded={props.isExpanded}
          />
        </EnableFor>
        <SideNavigationItem
          id='side-nav-projects'
          name={t('projects_header')}
          icon={FolderIcon}
          link={Home.Projects.List.All.withParams({ workspaceId: props.workspaceId })}
          highlightLink={Home.Projects.Path}
          disabled={false}
          isExpanded={props.isExpanded}
        />
        <EnableFor
          disableForEncryption
          hasPermission={jobPermissions.canList()}
        >
          <SideNavigationItem
            id='side-nav-models'
            name={t('models')}
            icon={ModelIcon}
            link={Home.Trainings.List.All.withParams({ workspaceId: props.workspaceId })}
            highlightLink={Home.Trainings.Path}
            disabled={false}
            isExpanded={props.isExpanded}
          />
        </EnableFor>

        <EnableFor
          disableForEncryption
          hasPermission={jobPermissions.canList()}
        >
          <SideNavigationItem
            id='side-nav-evaluations'
            name={t('evaluations')}
            icon={EvaluationIcon}
            link={Home.Inferences.List.All.withParams({ workspaceId: props.workspaceId })}
            highlightLink={Home.Inferences.Path}
            disabled={false}
            isExpanded={props.isExpanded}
          />
        </EnableFor>
        <EnableFor hasPermission={canViewAttachments}>
          <SideNavigationItem
            id='side-nav-attachments'
            name={t('attachments_header')}
            icon={AttachmentIcon}
            link={Home.Attachments.List.Folder.withParams({ workspaceId: props.workspaceId, attachmentId: '' })}
            highlightLink={Home.Attachments.Index}
            disabled={false}
            isExpanded={props.isExpanded}
          />
        </EnableFor>
        <SideNavigationSeparator />
        {props.userRole === UserRole.Administrator && (
          <SideNavigationItem
            id='side-nav-admin'
            name={t('admin_panel')}
            icon={PersonOutlineIcon}
            link={Home.Admin.Users.withParams({ workspaceId: props.workspaceId })}
            highlightLink={Home.Admin.Path}
            disabled={false}
            isExpanded={props.isExpanded}
          />
        )}
        <EnableFor hasPermission={canViewIntegrations}>
          <SideNavigationItem
            id='side-nav-api'
            name={t('api')}
            icon={CloudOutlineIcon}
            link={Home.Integrations.List.All.withParams({ workspaceId: props.workspaceId })}
            highlightLink={Home.Integrations.Path}
            disabled={false}
            isExpanded={props.isExpanded}
          />
        </EnableFor>
        <SideNavigationExternalItem
          id='side-nav-tutorials'
          name={t('manual')}
          icon={HelpOutlineIcon}
          link={ACDLIo.Tutorials.Path}
          isExpanded={props.isExpanded}
        />
      </div>
      <div className={classNames('side-navigation-footer', { expanded: props.isExpanded })}>
        {props.isExpanded && (
          <div className='side-navigation-logo'>
            <ZebraSmallLogo />
            <div>version 1.0.0</div>
          </div>
        )}
        <IconButton onClick={props.handleToggleExpand}>
          {props.isExpanded && <FirstPageIcon className='show-hide-icon' />}
          {!props.isExpanded && <LastPageIcon className='show-hide-icon' />}
        </IconButton>
      </div>
    </div>
  );
});
